import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Products', path: '/products' },
  { label: 'Collections', path: '/collections' },
  { label: 'About Us', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { totalItems, openCart } = useCart();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-12 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <div className="flex flex-col leading-none">
            <span
              className={`text-xl font-bold tracking-tight whitespace-nowrap transition-colors duration-500 ${
                scrolled ? 'text-[#2c2420]' : 'text-white'
              }`}
              style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '0.04em' }}
            >
              RETAILFLOW
            </span>
            <span
              className={`text-[10px] font-medium tracking-[0.25em] uppercase transition-colors duration-500 ${
                scrolled ? 'text-[#8B6348]' : 'text-white/70'
              }`}
            >
              LLC
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => {
            const active = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm tracking-widest uppercase font-medium transition-colors duration-200 whitespace-nowrap ${
                  scrolled
                    ? active
                      ? 'text-[#8B6348]'
                      : 'text-[#3a3228] hover:text-[#8B6348]'
                    : active
                    ? 'text-white'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        {/* Right Actions */}
        <div className="hidden md:flex items-center gap-3">
          {/* Cart Button */}
          <button
            onClick={openCart}
            className={`relative w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer ${
              scrolled
                ? 'text-[#3a3228] hover:bg-stone-100'
                : 'text-white hover:bg-white/15'
            }`}
            aria-label="Open cart"
          >
            <i className="ri-shopping-bag-line text-xl" />
            {totalItems > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-[#8B6348] text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                {totalItems > 99 ? '99+' : totalItems}
              </span>
            )}
          </button>
          <Link
            to="/contact"
            className={`whitespace-nowrap text-sm px-5 py-2.5 rounded-full font-medium tracking-wide transition-all duration-200 cursor-pointer ${
              scrolled
                ? 'bg-[#3a3228] text-white hover:bg-[#8B6348]'
                : 'bg-white/15 border border-white/40 text-white hover:bg-white/25'
            }`}
          >
            Book a Visit
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={openCart}
            className={`relative w-9 h-9 flex items-center justify-center cursor-pointer ${scrolled ? 'text-[#3a3228]' : 'text-white'}`}
            aria-label="Open cart"
          >
            <i className="ri-shopping-bag-line text-xl" />
            {totalItems > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] bg-[#8B6348] text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5">
                {totalItems > 99 ? '99+' : totalItems}
              </span>
            )}
          </button>
          <button
            className={`flex flex-col gap-1.5 cursor-pointer p-1 ${scrolled ? 'text-[#3a3228]' : 'text-white'}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block w-6 h-0.5 transition-all duration-200 ${scrolled ? 'bg-[#3a3228]' : 'bg-white'} ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block w-6 h-0.5 transition-all duration-200 ${scrolled ? 'bg-[#3a3228]' : 'bg-white'} ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-6 h-0.5 transition-all duration-200 ${scrolled ? 'bg-[#3a3228]' : 'bg-white'} ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 bg-white ${
          menuOpen ? 'max-h-96 border-t border-stone-100' : 'max-h-0'
        }`}
      >
        <div className="px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => {
            const active = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm tracking-widest uppercase font-medium cursor-pointer ${
                  active ? 'text-[#8B6348]' : 'text-[#3a3228] hover:text-[#8B6348]'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
          <Link
            to="/contact"
            className="whitespace-nowrap text-sm px-5 py-2.5 rounded-full bg-[#3a3228] text-white font-medium text-center mt-2 cursor-pointer hover:bg-[#8B6348] transition-colors"
          >
            Book a Visit
          </Link>
        </div>
      </div>
    </header>
  );
}
